// LLM generated - Model Claude 4 Sonnet
import { createStyles } from '@mantine/core';

export type PromoCardVariant = 'default' | 'fat' | 'entry-submission' | 'board';

export const useStyles = createStyles(
  (theme, params: { variant?: PromoCardVariant; showBorder?: boolean; isCompact?: boolean }) => {
    const isFat = params.variant === 'fat';
    const isDark = params.variant === 'entry-submission';
    const isBoard = params.variant === 'board';
    const isCompact = params.isCompact;
    const valueGradient = `linear-gradient(90deg, ${theme.colors.blue[7]} 0%, ${theme.colors.pink[7]} 100%)`;

    const tokens = {
      surface: {
        height: isFat ? 72 : theme.other.spacing._64,
        bg: isDark ? theme.colors.gray2[7] : theme.white,
        // eslint-disable-next-line no-nested-ternary
        borderColor: isDark
          ? theme.colors.gray2[6]
          : params.showBorder
            ? theme.colors.gray2[1]
            : 'transparent',
        // eslint-disable-next-line no-nested-ternary
        hoverBorder: isDark
          ? theme.colors.gray2[5]
          : params.showBorder
            ? theme.colors.gray2[3]
            : theme.colors.gray2[1],
      },
      text: {
        title: isDark ? theme.white : theme.colors.gray2[6],
        description: isDark ? theme.colors.gray2[3] : theme.colors.gray2[4],
      },
      pill: {
        bg: isDark ? theme.colors.gray2[6] : theme.colors.gray2[0],
        color: isDark ? theme.white : theme.colors.gray2[6],
        hoverBg: isDark ? theme.colors.gray2[5] : theme.colors.gray2[1],
        removeBg: isDark ? 'rgba(219, 59, 59, 0.16)' : 'transparent',
      },
      iconShadow: isDark
        ? 'drop-shadow(0px 0px 12px rgba(26, 28, 30, 0.2))'
        : undefined,
      chip: {
        bg: isDark
          ? `linear-gradient(90deg, rgba(127, 26, 147, 0.15) 0%, rgba(127, 26, 147, 0.15) 100%), linear-gradient(90deg, ${theme.colors.gray2[6]} 0%, ${theme.colors.gray2[6]} 100%)`
          : `linear-gradient(90deg, rgba(127, 26, 147, 0.1) 0%, rgba(127, 26, 147, 0.1) 100%), linear-gradient(90deg, ${theme.white} 0%, ${theme.white} 100%)`,
        expiredBg: isDark
          ? `linear-gradient(90deg, rgba(219, 59, 59, 0.16) 0%, rgba(219, 59, 59, 0.16) 100%), linear-gradient(90deg, ${theme.colors.gray2[6]} 0%, ${theme.colors.gray2[6]} 100%)`
          : `linear-gradient(90deg, rgba(219, 59, 59, 0.16) 0%, rgba(219, 59, 59, 0.16) 100%), linear-gradient(90deg, ${theme.white} 0%, ${theme.white} 100%)`,
      },
    };

    return {
      root: {
        position: 'relative',
        overflow: 'visible',
        alignSelf: 'flex-start',
        width: '100%',
      },

      surface: {
        backgroundColor: tokens.surface.bg,
        borderRadius: theme.other.spacing._16,
        border: `1px solid ${tokens.surface.borderColor}`,
        padding: `0 ${theme.other.spacing._16}px`,
        height: tokens.surface.height,
        minHeight: tokens.surface.height,
        display: 'flex',
        alignItems: 'center',
        minWidth: isCompact ? '300px' : undefined,
        maxWidth: isCompact ? '350px' : undefined,
        flexShrink: isBoard ? 0 : undefined,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
      },

      clickable: {
        cursor: 'pointer',
        '&:hover': {
          borderColor: tokens.surface.hoverBorder,
          transition: 'border-color 0.2s ease',
        },
        '&:active': { transform: 'scale(0.99)' },
      },

      content: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
      },

      leftSection: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        gap: theme.other.spacing._8,
      },

      titleStack: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        gap: theme.other.spacing._4,
      },

      leftContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        minWidth: `${theme.other.spacing._64}px`,
        height: `${theme.other.spacing._32}px`,
      },

      iconContainer: {
        ...(tokens.iconShadow && { filter: tokens.iconShadow }),
      },

      valueText: {
        fontFamily: theme.other.fontFamily.hubotSansBlack,
        fontWeight: 400,
        fontSize: '28px',
        lineHeight: '32px',
        whiteSpace: 'nowrap',
        backgroundImage: valueGradient,
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        textBoxTrim: 'trim-both',
        textBoxEdge: 'text text',
        margin: 0,
        padding: 0,
        paddingTop: theme.other.spacing._4,
        '@supports not (text-box-trim: trim-both)': {
          marginTop: 0,
        },
      },

      valueTextCompact: {
        fontSize: '22px',
        lineHeight: '26px',
      },

      textContentClickable: { cursor: 'pointer' },

      title: {
        color: tokens.text.title,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textBoxTrim: 'trim-both',
        textBoxEdge: 'text text',
      },

      description: {
        color: tokens.text.description,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textBoxTrim: 'trim-both',
        textBoxEdge: 'text text',
      },

      expirationText: {
        color: theme.colors.pink[7],
      },

      applyPill: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '56px',
        padding: `${theme.other.spacing._8}px ${theme.other.spacing._12}px`,
        borderRadius: theme.other.spacing._8,
        backgroundColor: tokens.pill.bg,
        color: tokens.pill.color,
        border: 'none',
        cursor: 'default',
        textAlign: 'center',
      },

      applyPillClickable: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: tokens.pill.hoverBg,
          transition: 'background-color 0.2s ease',
        },
      },

      applyPillRemove: {
        minWidth: '56px',
        padding: `${theme.other.spacing._8}px ${theme.other.spacing._12}px`,
        backgroundColor: tokens.pill.removeBg,
        color: theme.colors.red[3],

        '&:hover': {
          backgroundColor: 'rgba(219, 59, 59, 0.16)',
        },
      },

      applyPillApplied: {
        backgroundColor: 'transparent',
        color: theme.colors.gray2[3],
      },

      expirationChip: {
        position: 'absolute',
        top: `-${theme.other.spacing._8}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        height: `${theme.other.spacing._16}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `0 ${theme.other.spacing._8}px`,
        borderRadius: theme.other.spacing._16,
        backgroundImage: tokens.chip.bg,
        color: theme.colors.pink[7],
        whiteSpace: 'nowrap',
        zIndex: 3,
      },

      expirationChipExpired: {
        backgroundImage: tokens.chip.expiredBg,
        color: theme.colors.red[4],
      },

      disabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        '&:hover': {
          borderColor: tokens.surface.hoverBorder,
          transform: 'none',
        },
      },
    };
  }
);
